/**
 * Apiboo Collection Format v1.0 — the reader.
 *
 * Near-passthrough on purpose: the file IS the normalized shape
 * (collectionImport/types.js), so "parsing" is sanitation, not translation.
 * Every coercion below exists for the hostile-file case — a hand-edited or
 * generated envelope must degrade field-by-field, never crash the modal.
 *
 * ## The version gate, and why it is asymmetric
 *
 * `version` is "MAJOR.MINOR". A newer MINOR is accepted and unknown fields are
 * simply not imported — minor bumps are additive by the format's contract, so
 * the worst case is a request arriving without some new nicety. A newer MAJOR
 * is REFUSED OUTRIGHT with a version-naming error. The distinction matters
 * because this file carries auth config and scripts: half-importing a shape we
 * misread does not produce an obviously broken collection, it produces a
 * subtly wrong one — an auth block interpreted under old rules, a script
 * running in the wrong dialect. A refusal the user can read beats a corruption
 * they will not notice.
 */

const str = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));

const kvRows = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .filter((r) => r && typeof r === 'object')
    .map((r) => ({ key: str(r.key), value: str(r.value), enabled: r.enabled !== false }));

const plainObj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : undefined);

/** Is this JSON value an Apiboo collection envelope? (Cheap; used by detect.) */
export function isApibooCollectionEnvelope(data) {
  return !!(data && typeof data === 'object' && data.apiboo && data.apiboo.format === 'collection');
}

function parseVersion(raw) {
  const m = /^(\d+)\.(\d+)$/.exec(str(raw).trim());
  if (!m) return null;
  return { major: Number(m[1]), minor: Number(m[2]) };
}

function normalizeRequest(req, index) {
  if (!req || typeof req !== 'object') return null;
  const out = {
    name: str(req.name) || 'Request',
    description: str(req.description),
    method: (str(req.method) || 'GET').toUpperCase(),
    url: str(req.url),
    headers: kvRows(req.headers),
    params: kvRows(req.params),
    cookies: kvRows(req.cookies),
    body: str(req.body),
    body_type: str(req.body_type) || 'json',
    auth: plainObj(req.auth) || {},
    sort_order: Number.isFinite(req.sort_order) ? req.sort_order : index,
  };
  if (plainObj(req.settings)) out.settings = req.settings;
  if (Array.isArray(req.formDataFields)) out.formDataFields = req.formDataFields;
  if (req.folder_id != null && str(req.folder_id)) out.folder_id = str(req.folder_id);
  if (str(req.preRequestScript)) out.preRequestScript = str(req.preRequestScript);
  if (str(req.testScript)) out.testScript = str(req.testScript);
  // Only the two dialects the runner knows. A future third dialect arrives via
  // a version bump, not by letting arbitrary strings through to the executor.
  if (req.scriptDialect === 'pm' || req.scriptDialect === 'boo') {
    out.scriptDialect = req.scriptDialect;
  }
  if (plainObj(req.protocolProfileBehavior)) out.protocolProfileBehavior = req.protocolProfileBehavior;
  if (plainObj(req.mock)) out.mock = req.mock;
  return out;
}

function normalizeFolder(folder, index) {
  if (!folder || typeof folder !== 'object') return null;
  const id = str(folder.id);
  if (!id) return null; // an id-less folder can never be referenced — drop it
  const out = {
    id,
    name: str(folder.name) || 'Folder',
    parent_id: folder.parent_id != null && str(folder.parent_id) ? str(folder.parent_id) : null,
    sort_order: Number.isFinite(folder.sort_order) ? folder.sort_order : index,
  };
  if (plainObj(folder.auth)) out.auth = folder.auth;
  if (str(folder.preRequestScript)) out.preRequestScript = str(folder.preRequestScript);
  if (str(folder.testScript)) out.testScript = str(folder.testScript);
  if (Array.isArray(folder.variables)) out.variables = folder.variables;
  return out;
}

/**
 * Envelope → NormalizedCollection[].
 * Throws only for the two states a caller must show the user verbatim:
 * a missing/unparseable version, or a major we do not speak.
 */
export function parseApiboo(data) {
  const version = parseVersion(data?.apiboo?.version);
  if (!version) {
    throw new Error('Apiboo export has no readable version — expected e.g. "1.0".');
  }
  if (version.major !== 1) {
    throw new Error(
      `This Apiboo export is format version ${version.major}.${version.minor}, but this build reads version 1.x. ` +
        'Update the app to import it — a partial read could silently misinterpret auth or scripts.'
    );
  }

  const collections = [];
  const list = Array.isArray(data.collections) ? data.collections : [];
  for (const c of list) {
    if (!c || typeof c !== 'object') continue;
    const folders = (Array.isArray(c.folders) ? c.folders : [])
      .map(normalizeFolder)
      .filter(Boolean);
    // A folder_id pointing at nothing (hand-edited file) must not strand the
    // request somewhere unreachable — same rule the sidebar's own folders
    // follow: degrade to the collection root, keep the request.
    const known = new Set(folders.map((f) => f.id));
    const requests = (Array.isArray(c.requests) ? c.requests : [])
      .map(normalizeRequest)
      .filter(Boolean)
      .map((r) => (r.folder_id && !known.has(r.folder_id) ? { ...r, folder_id: undefined } : r));

    const col = {
      name: str(c.name) || 'Imported collection',
      description: str(c.description),
      requests,
      folders,
    };
    if (plainObj(c.settings)) col.settings = c.settings;
    if (str(c.preRequestScript)) col.preRequestScript = str(c.preRequestScript);
    if (str(c.testScript)) col.testScript = str(c.testScript);
    collections.push(col);
  }
  return collections;
}
